/**
 * BuyerFactoryBoundary (AJIP-16.02.009 · §22)
 * ---------------------------------------------------------------------
 * Correction 2 · retained solely to ENFORCE journey separation between
 * Buyer and Factory instances (§22).
 *
 * The boundary is:
 *   • Validation-only.
 *   • Provider-independent.
 *   • Free of external-system imports.
 *   • Free of storage access.
 *   • Free of customer-facing behaviour.
 *   • Without authority over eligibility, activation, progression or
 *     completion.
 *
 * Purpose per §22: reject cross-journey reads and provide the ONLY
 * authorised surface through which a Factory Journey may record that
 * a Buyer signal exists — as an opaque, validated reference — without
 * loading Buyer state or inferring Buyer decisions.
 */
import { BuyerFactoryBoundaryError } from './FactoryJourneyErrors';

const MAX_REF_LEN = 128;
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@\-/]{0,127}$/;

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const validateBuyerReferenceRef = (buyerReferenceRef) => {
  if (!isNonEmptyString(buyerReferenceRef)) {
    throw new BuyerFactoryBoundaryError('buyerReferenceRef must be non-empty string');
  }
  if (buyerReferenceRef.length > MAX_REF_LEN) {
    throw new BuyerFactoryBoundaryError('buyerReferenceRef exceeds max length');
  }
  if (!REF_PATTERN.test(buyerReferenceRef)) {
    throw new BuyerFactoryBoundaryError('buyerReferenceRef has invalid shape');
  }
  return true;
};

const attachBuyerReference = ({ factoryContext, buyerReferenceRef }) => {
  validateBuyerReferenceRef(buyerReferenceRef);
  return Object.freeze({ ...(factoryContext || {}), buyerReferenceRef });
};

const BOUNDARY_GOVERNANCE = Object.freeze({
  factory_may: 'record-opaque-buyer-reference',
  factory_shall_never: 'resolve|load|read-buyer-context|infer-buyer-decision|complete-via-buyer-signal',
});

const BOUNDARY_NAME = 'BuyerFactoryBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  validateBuyerReferenceRef,
  attachBuyerReference,
};
